import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../utils/AuthContext/AuthContext';
import LogoutButton from './LogoutButton';
import FormsSVG from '../assets/Forms';
import LogoutSVG from '../assets/Logout';
import PersonSVG from '../assets/Person';
import { useMediaQuery } from 'react-responsive';
import config from '../utils/config';

const SettingsModal = ({ isOpen, onClose, user: propUser }) => {
    const [activeTab, setActiveTab] = useState('profile');
    const { logout, token, user: contextUser } = useAuth();
    const modalRef = useRef(null);
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const [userProfile, setUserProfile] = useState({ 
        firstName: '', 
        lastName: '', 
        email: '', 
        phone: '', 
        address: '' 
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isModified, setIsModified] = useState(false);

    // Use either prop user or context user
    const user = propUser || contextUser;

    // Debug logging
    // console.log('SettingsModal user state:', { propUser, contextUser, combinedUser: user });

    useEffect(() => {
        // Reset states when modal opens
        if (isOpen) {
            setLoading(true);
            setError('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        // If we have a user object with data, use it directly
        if (user && user._id) {
            console.log('Using existing user data:', user);
            setUserProfile({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                phone: user.phone || '',
                address: user.address || ''
            });
            setLoading(false);
            return;
        }

        // If no user data, show error
        if (!user) {
            console.log('No user available in SettingsModal');
            setError('User not authenticated');
            setLoading(false);
            return;
        }

        // If we have a user but no _id, show error
        if (!user._id && !user.id) {
            console.log('No userId found in SettingsModal:', user);
            setError('User ID not found');
            setLoading(false);
            return;
        }

        // Fetch profile data
        async function fetchData() {
            try {
                const userId = user._id || user.id;
                const response = await fetch(`${config.apiBaseUrl}/profile/${userId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const data = await response.json();
                setUserProfile(data);
            } catch (error) {
                console.error('Error fetching profile:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [isOpen, token, user, propUser, contextUser]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserProfile(prevState => ({
            ...prevState,
            [name]: value
        }));
        setIsModified(true);
    };

    const handleCancel = () => {
        if (!user) return;
        
        setIsModified(false);
        // Re-fetch the user profile to reset the form
        async function fetchData() {
            try {
                const userId = user._id || user.id;
                if (!userId) {
                    setError('User ID not found');
                    return;
                }

                const response = await fetch(`${config.apiBaseUrl}/profile/${userId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const data = await response.json();
                setUserProfile(data);
            } catch (error) {
                setError(error.message);
            }
        }

        fetchData();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user) return;

        try {
            const userId = user._id || user.id;
            if (!userId) {
                setError('User ID not found');
                return;
            }

            const response = await fetch(`${config.apiBaseUrl}/profile/${userId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(userProfile)
            });
            if (!response.ok) {
                throw new Error('Failed to update profile');
            }
            alert('Profile updated successfully');
            setIsModified(false);
        } catch (error) {
            setError('Failed to update profile');
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const renderContent = () => {
        switch (activeTab) {
            case 'profile':
                return (
                    <div className="p-6 overflow-y-auto max-h-[600px]">
                        <h2 className="text-xl font-semibold mb-6">Profile</h2>
                        {loading ? (
                            <div className="flex justify-center items-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-shi-green"></div>
                            </div>
                        ) : error ? (
                            <div className="text-red-500 p-4 bg-red-50 rounded-lg">
                                {error}
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={userProfile.firstName || ''}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-shi-green focus:border-shi-green"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={userProfile.lastName || ''}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-shi-green focus:border-shi-green"
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={userProfile.email || ''}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-shi-green focus:border-shi-green"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={userProfile.phone || ''}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-shi-green focus:border-shi-green"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                                    <input
                                        type="text"
                                        name="address"
                                        value={userProfile.address || ''}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-shi-green focus:border-shi-green"
                                    />
                                </div>

                                {isModified && (
                                    <div className="flex justify-end gap-4 mt-6">
                                        <button
                                            type="button"
                                            onClick={handleCancel}
                                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-shi-green text-white rounded-lg hover:bg-green-950"
                                        >
                                            Save Changes
                                        </button>
                                    </div>
                                )}
                            </form>
                        )}
                    </div>
                );
            case 'privacy':
                return (
                    <div className="p-6 overflow-y-auto max-h-[600px]">
                        <h2 className="text-xl font-semibold mb-4">Privacy Policy</h2>
                        <div className="text-sm text-gray-600 space-y-4">
                            <p>
                                This Privacy Policy describes Shishito's approach to Privacy and provides information about how Shishito collects, uses, processes, and discloses personal information.
                                If you have questions about this Privacy Policy or would like to exercise any of your privacy rights, you may contact Customer Care at <span className="text-black font-bold underline">info@shishito-re.com</span>.
                            </p>
                            
                            <h3 className="text-lg font-semibold text-black">What This Privacy Policy Covers</h3>
                            <p>
                                This Privacy Policy describes how Shishito-re.com uses personal information in connection with goods and services sold by Shishito, our apps, and the use of the Shishito-re.com website unless otherwise listed below.
                            </p>

                            <h3 className="text-lg font-semibold text-black">Shishito's Approach to Privacy</h3>
                            <p>
                                Shishito takes your privacy seriously and is mindful of potential concerns about how we use the personal information we collect from and about you. In addition to this Privacy Policy, we have internal policies and practices designed to keep your personal information secure.
                            </p>

                            <h3 className="text-lg font-semibold text-black">Your Privacy Rights</h3>
                            <p>
                                You have the right to:
                            </p>
                            <ul className="list-disc ml-6 space-y-2">
                                <li>Access your personal information</li>
                                <li>Correct inaccurate information</li>
                                <li>Request deletion of your information</li>
                                <li>Opt-out of marketing communications</li>
                                <li>Control cookie preferences</li>
                            </ul>

                            <p className="mt-4">
                                To exercise these rights or learn more about our privacy practices, please visit our full <a href="/privacy" className="text-blue-600 hover:underline">Privacy</a> or contact us at info@shishito-re.com.
                            </p>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div ref={modalRef} className="bg-white rounded-xl w-[90%] md:w-[800px] h-[90vh] md:h-[600px] flex flex-col md:flex-row overflow-hidden relative">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-2 right-2 md:top-4 md:right-4 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors z-10"
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M18 6L6 18M6 6L18 18"
                            stroke="#044225"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

                {/* Mobile Navigation Tabs */}
                <div className="md:hidden flex border-b border-gray-200 pt-12">
                    <button
                        onClick={() => setActiveTab('profile')}
                        className={`flex-1 px-4 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 ${
                            activeTab === 'profile'
                                ? 'bg-shi-light-mysterious-green text-shi-deep-purple'
                                : 'text-shi-green hover:bg-shi-green/10'
                        }`}
                    >
                        <PersonSVG width={18} height={18} fill={activeTab === 'profile' ? "#30012A" : "#044225"} />
                        Profile
                    </button>
                    <button
                        onClick={() => setActiveTab('privacy')}
                        className={`flex-1 px-4 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 ${
                            activeTab === 'privacy'
                                ? 'bg-shi-light-mysterious-green text-shi-deep-purple'
                                : 'text-shi-green hover:bg-shi-green/10'
                        }`}
                    >
                        <FormsSVG width={18} height={18} fill={activeTab === 'privacy' ? "#30012A" : "#044225"} />
                        Privacy
                    </button>
                </div>

                {/* Desktop Left Navigation */}
                <div className="hidden md:flex w-48 bg-white border-r border-gray-200 flex-col justify-between p-4">
                    <div className="space-y-2">
                        <button
                            onClick={() => setActiveTab('profile')}
                            className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-[10px] ${
                                activeTab === 'profile'
                                    ? 'bg-shi-light-mysterious-green text-shi-deep-purple'
                                    : 'text-shi-green hover:bg-shi-green/10'
                            }`}
                        >
                            <PersonSVG width={18} height={18} fill={activeTab === 'profile' ? "#30012A" : "#044225"} />
                            Profile
                        </button>
                    </div>
                    <div className="space-y-2">
                        <button
                            onClick={() => setActiveTab('privacy')}
                            className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-[10px] ${
                                activeTab === 'privacy'
                                    ? 'bg-shi-light-mysterious-green text-shi-deep-purple'
                                    : 'text-shi-green hover:bg-shi-green/10'
                            }`}
                        >
                            <FormsSVG width={18} height={18} fill={activeTab === 'privacy' ? "#30012A" : "#044225"} />
                            Privacy
                        </button>
                        <div className="w-full text-left px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-[10px] text-shi-green hover:bg-shi-green/10">
                            <LogoutSVG width={18} height={18} fill="#044225" />
                            <LogoutButton className="!p-0 !w-auto" />
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 bg-slate-50 overflow-y-auto">
                    {renderContent()}
                </div>

                {/* Mobile Logout Button */}
                <div className="md:hidden border-t border-gray-200 p-4">
                    <div className="w-full text-left px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-[10px] text-shi-green hover:bg-shi-green/10">
                        <LogoutSVG width={18} height={18} fill="#044225" />
                        <LogoutButton className="!p-0 !w-auto" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsModal; 